import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDocFromServer, serverTimestamp, setDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import backgroundImage from '../../assets/common/background_homepage.png';
import termsConditionsSvg from '../../assets/common/terms_and_conditions_title.svg';
import { db } from '../../firebase';
import './TermsAcceptance.css';

export default function TermsAcceptance() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  const { currentUser, loading: authLoading, currentRole, userRoles } = useAuth();

  const nextPath = useMemo(() => {
    const stateNext = location?.state?.next;
    if (typeof stateNext === 'string' && stateNext.startsWith('/')) return stateNext;
    return '/kyc';
  }, [location]);

  const [loadingDoc, setLoadingDoc] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    // Read-only mode when not signed in (e.g. from Homepage/AuthChoice)
    if (!currentUser) {
      setLoadingDoc(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingDoc(true);
      setError('');
      try {
        const snap = await getDocFromServer(doc(db, 'users', currentUser.uid));
        const data = snap.exists() ? snap.data() : {};
        const isAccepted = !!(data?.termsAccepted || data?.terms_accepted || data?.termsAndConditionsAccepted);
        if (!cancelled) {
          setAlreadyAccepted(isAccepted);
          setAccepted(isAccepted);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || t('terms.errors.failedToLoad'));
      } finally {
        if (!cancelled) setLoadingDoc(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [authLoading, currentUser, t]);

  useEffect(() => {
    // If already accepted, auto-continue (only if user is signed in)
    if (!authLoading && currentUser && !loadingDoc && alreadyAccepted) {
      navigate(nextPath, { replace: true });
    }
  }, [authLoading, currentUser, loadingDoc, alreadyAccepted, navigate, nextPath]);

  const handleAccept = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const role = currentRole || (userRoles || [])[0] || null;
      await setDoc(
        doc(db, 'users', currentUser.uid),
        {
          termsAccepted: true,
          termsAcceptedAt: serverTimestamp(),
          termsVersion: 1,
          termsAcceptedRole: role
        },
        { merge: true }
      );
      navigate(nextPath, { replace: true });
    } catch (e) {
      setError(e?.message || t('terms.errors.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`terms-page ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="terms-bg" aria-hidden="true" style={{ backgroundImage: `url(${backgroundImage})` }} />

      <div className="terms-card" role="main" aria-label={t('terms.title')}>
        {/* <div className="terms-language-switcher">
          <LanguageSwitcher variant="welcome" />
        </div> */}

        <div className="terms-header">
          <img className="terms-title-img" src={termsConditionsSvg} alt={t('terms.title')} />
          <p className="terms-subtitle">{t('terms.subtitle')}</p>
        </div>

        {error && <div className="terms-error">{error}</div>}

        <div className="terms-content" aria-busy={loadingDoc ? 'true' : 'false'}>
          {loadingDoc ? (
            <div className="terms-loading">
              <div className="terms-spinner" aria-hidden="true" />
              <p>{t('common.loading')}</p>
            </div>
          ) : (
            <>
              <p className="terms-paragraph">{t('terms.intro')}</p>
              <div className="terms-box">
                <h3 className="terms-section-title">{t('terms.sections.title')}</h3>
                <ul className="terms-list">
                  <li>{t('terms.sections.item1')}</li>
                  <li>{t('terms.sections.item2')}</li>
                  <li>{t('terms.sections.item3')}</li>
                  <li>{t('terms.sections.item4')}</li>
                </ul>
                <p className="terms-footnote">{t('terms.footerNote')}</p>
              </div>

              {currentUser ? (
                <div className="terms-accept-row">
                  <label className="terms-checkbox">
                    <input
                      type="checkbox"
                      checked={accepted}
                      onChange={(e) => setAccepted(e.target.checked)}
                      disabled={saving}
                    />
                    <span>{t('terms.acceptLabel')}</span>
                  </label>

                  <button
                    type="button"
                    className="terms-accept-btn"
                    onClick={handleAccept}
                    disabled={!accepted || saving}
                  >
                    {saving ? (
                      <span className="terms-btn-loading">
                        <span className="terms-btn-spinner" aria-hidden="true" />
                        {t('terms.accepting')}
                      </span>
                    ) : (
                      t('terms.acceptCta')
                    )}
                  </button>
                </div>
              ) : (
                <div className="terms-accept-row">
                  <button type="button" className="terms-accept-btn" onClick={() => navigate('/login')}>
                    {t('terms.signInToAccept')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


